import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { Observable } from 'rxjs/internal/Observable';
import { FileItem, FileUploader, ParsedResponseHeaders } from 'ng2-file-upload';
import { BehaviorSubject, Subject } from 'rxjs';
import { MainService } from '@app/services/main.service';
import { BackendService } from '@app/services/backend.service';
import { UsersService } from '@app/services/users.service';
import { SnackbarService } from '@app/services/snackbar.service';

// @API files/upload.post
const FILE_UPLOADER = new FileUploader({
    url: environment.url.backendApi + '/files/upload',
    maxFileSize: 35 * 1024 * 1024,
    autoUpload: true,
    removeAfterUpload: true,
    itemAlias: 'uri',
    queueLimit: 10,
});

export interface FileUploadResponse {
    contentType: string;
    id: string;
    size: number;
    originalname?: string;
}

export function isFileUploadResponse(object: unknown): object is FileUploadResponse {
    return typeof object === 'object' && 'contentType' in object && 'id' in object && 'size' in object;
}

@Injectable({
    providedIn: 'root',
})
export class FileUploaderService {
    public readonly mainService = inject(MainService);
    private readonly backendService = inject(BackendService);
    private readonly usersService = inject(UsersService);
    private readonly snackbarService = inject(SnackbarService);

    private uploaderSource: BehaviorSubject<FileUploader> = new BehaviorSubject<FileUploader>(FILE_UPLOADER);
    public uploaderObservable: Observable<FileUploader> = this.uploaderSource.asObservable();

    fileUploadedSubject: Subject<{ item: FileItem; response: FileUploadResponse }> = new Subject();
    public fileUploadedObservable: Observable<{ item: FileItem; response: FileUploadResponse }> =
        this.fileUploadedSubject.asObservable();
    fileAddedSubject: Subject<FileItem> = new Subject();
    public fileAddedObservable: Observable<FileItem> = this.fileAddedSubject.asObservable();
    completeAllSubject: Subject<void> = new Subject();
    public completeAllObservable: Observable<void> = this.completeAllSubject.asObservable();

    private uploader: FileUploader;

    constructor() {
        this.uploader = this.uploaderSource.getValue();
        this.setUploaderEvents();
    }

    public setUploaderEvents(): void {
        this.uploader.onWhenAddingFileFailed = (fileLike: any) => {
            if (fileLike.size > this.uploader.options.maxFileSize) {
                this.snackbarService.showError(
                    'Soubor ' + fileLike.name + ' je příliš velký. Maximální velikost souboru může být 35 MB'
                );
            }
        };
        this.uploader.onBeforeUploadItem = (fileItem) => {
            // fileItem.withCredentials = false;
            let authToken = this.usersService.accessToken;
            if (this.backendService.lsIsAvailable) {
                authToken = localStorage.getItem('feathers-jwt');
            }
            let uploaderOptions = this.uploader.options;
            uploaderOptions = {
                ...uploaderOptions,
                authToken,
            };
            fileItem.file.name = encodeURIComponent(fileItem.file.name);
            this.uploader.setOptions(uploaderOptions);
        };

        this.uploader.onAfterAddingFile = (fileItem) => {
            this.fileAddedSubject.next(fileItem);
        };

        this.uploader.onCompleteAll = () => {
            this.completeAllSubject.next();
        };

        this.uploader.onSuccessItem = (
            item: FileItem,
            response: string,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            status: number,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            headers: ParsedResponseHeaders
        ) => {
            this.fileUploadedSubject.next({ item, response: JSON.parse(response) });
        };

        this.uploader.onCancelItem = (
            item: FileItem,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            response: string,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            status: number,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            headers: ParsedResponseHeaders
        ) => {
            this.snackbarService.showError(
                'Nahrávání souboru ' +
                    (item.file.name.length > 50 ? item.file.name.slice(0, 50) : item.file.name) +
                    ' bylo přerušeno...'
            );
        };

        this.uploader.onErrorItem = (
            item: FileItem,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            response: string,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            status: number,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            headers: ParsedResponseHeaders
        ) => {
            this.snackbarService.showError(
                'Nastala chyba při nahrávání souboru ' +
                    (item.file.name.length > 50 ? item.file.name.slice(0, 50) : item.file.name)
            );
        };
    }
}
